import { useState, useEffect, useCallback } from 'react';
import { apiCall } from './api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const usePushNotifications = (token) => {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState('default');

  useEffect(() => {
    // Check if push notifications are supported
    const supported = 'serviceWorker' in navigator && 'PushManager' in window;
    setIsSupported(supported);
    
    if (supported) {
      setPermission(Notification.permission);
      checkSubscription();
    }
  }, []);

  const checkSubscription = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const sub = await registration.pushManager.getSubscription();
      setIsSubscribed(!!sub);
      setSubscription(sub);
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  const registerServiceWorker = async () => {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      console.log('Service Worker registered:', registration);
      return registration;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      throw error;
    }
  };

  const requestPermission = async () => {
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      return result;
    } catch (error) {
      console.error('Permission request failed:', error);
      return 'denied';
    }
  };

  const subscribe = useCallback(async () => {
    if (!isSupported) {
      console.warn('Push notifications not supported');
      return false;
    }

    try {
      // Request permission if needed
      if (Notification.permission === 'default') {
        const result = await requestPermission();
        if (result !== 'granted') {
          console.warn('Permission denied');
          return false;
        }
      } else if (Notification.permission === 'denied') {
        console.warn('Permission previously denied');
        return false;
      }

      // Register service worker
      await registerServiceWorker();
      const registration = await navigator.serviceWorker.ready;

      // Get VAPID key from server
      let vapidKey = '';
      try {
        const response = await fetch(`${API_URL}/api/push/vapid-key`);
        const data = await response.json();
        vapidKey = data.vapid_key;
      } catch (e) {
        console.warn('Could not get VAPID key, using demo mode');
      }

      // Subscribe to push
      const sub = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: vapidKey || undefined
      });

      setSubscription(sub);
      setIsSubscribed(true);

      // Send subscription to server
      if (token) {
        await apiCall('POST', '/push/subscribe', {
          endpoint: sub.endpoint,
          keys: {
            p256dh: btoa(String.fromCharCode(...new Uint8Array(sub.getKey('p256dh')))),
            auth: btoa(String.fromCharCode(...new Uint8Array(sub.getKey('auth'))))
          }
        }, token);
      }

      return true;
    } catch (error) {
      console.error('Subscription failed:', error);
      return false;
    }
  }, [isSupported, token]);

  const unsubscribe = useCallback(async () => {
    try {
      if (subscription) {
        await subscription.unsubscribe();
      }
      
      if (token) {
        await apiCall('DELETE', '/push/unsubscribe', null, token);
      }

      setSubscription(null);
      setIsSubscribed(false);
      return true;
    } catch (error) {
      console.error('Unsubscribe failed:', error);
      return false;
    }
  }, [subscription, token]);

  const showLocalNotification = useCallback(async (title, options = {}) => {
    if (!isSupported || Notification.permission !== 'granted') {
      console.warn('Cannot show notification');
      return;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      await registration.showNotification(title, {
        icon: '/logo192.png',
        badge: '/badge.png',
        vibrate: [100, 50, 100],
        ...options
      });
    } catch (error) {
      console.error('Failed to show notification:', error);
    }
  }, [isSupported]);

  return {
    isSupported,
    isSubscribed,
    permission,
    subscribe,
    unsubscribe,
    showLocalNotification,
    requestPermission
  };
};

export default usePushNotifications;
